/**
 * Accès aux atlas d'icônes.
 *
 * Les icônes Dofus font toutes la même taille, donc l'atlas est une grille
 * régulière et la position se déduit de l'index, si bien que les métadonnées se réduisent
 * à {id: index}. Un atlas = une requête au lieu de plusieurs centaines.
 */

interface Feuille {
  colonnes: number
  taille: number
  /** icônes par feuille ; absent sur les atlas d'une seule pièce */
  parFeuille?: number
  feuilles?: number
  index: Record<string, number>
}

type NomFeuille = "monstres" | "items" | "classes" | "ui"

interface Atlas extends Partial<Record<NomFeuille, Feuille>> {
  /** monstres dont le renderer d'Ankama n'a pas servi de sprite */
  combat?: { absents: string[] }
}

let atlas: Atlas = {} as Atlas

export async function chargerAtlas(): Promise<void> {
  try {
    atlas = await (await fetch("/img/atlas.json")).json()
  } catch {
    atlas = {} as Atlas
  }
}

/**
 * Style inline positionnant une icône de l'atlas, redimensionnée à `px`.
 * Renvoie une chaîne vide si l'icône est absente. L'appelant affiche alors
 * son repli, jamais une image cassée.
 */
export function icone(feuille: NomFeuille, cle: string | number, px = 48): string {
  const f = atlas[feuille]
  const n = f?.index[String(cle)]
  if (!f || n === undefined) return ""
  // Un atlas trop grand dépasse la limite de 4096 px par côté d'iOS : au-delà
  // d'une certaine taille il est découpé en feuilles. L'index reste global, la
  // feuille et la position s'en déduisent.
  const par = f.parFeuille ?? Infinity
  const lot = Math.floor(n / par)
  const local = n % par
  const col = local % f.colonnes
  const ligne = Math.floor(local / f.colonnes)
  return [
    `width:${px}px`,
    `height:${px}px`,
    `background-image:url(/img/${feuille}${lot > 0 ? `-${lot}` : ""}.webp)`,
    `background-size:${f.colonnes * px}px auto`,
    `background-position:${-col * px}px ${-ligne * px}px`,
  ].join(";")
}


/**
 * Le renderer d'Ankama ne sert pas le look de tous les monstres : ceux du
 * contenu le plus récent reviennent en aplat d'erreur, et leur sprite n'existe
 * donc pas. L'arène retombe alors sur la vignette d'atlas, agrandie, plutôt que
 * de laisser un vide.
 */
export const aSprite = (id: number | string): boolean =>
  !atlas.combat?.absents.includes(String(id))

/** Fond de l'adversaire dans l'arène : son sprite, ou sa vignette agrandie. */
export function fondMonstre(id: number | string, px = 96): string {
  if (aSprite(id)) return `background-image:url(/img/combat/m${id}.webp)`
  const st = icone("monstres", id, px)
  return st ? `${st};background-repeat:no-repeat;background-position:center bottom` : ""
}

/** Tête de classe : mâle = 0, femelle = 1. */
export const cleClasse = (classeId: number, sexe: 0 | 1 = 0) => `${classeId}-${sexe}`

/**
 * Icône d'interface Dofus (statistiques, collections).
 * Rend une balise complète : ces icônes accompagnent toujours un libellé.
 */
export function ic(cle: string, px = 20): string {
  const st = icone("ui", cle, px)
  return st ? `<i class="dic" style="${st}"></i>` : ""
}
